import React, { useEffect, useRef, useState } from 'react'
import { 
  RiDeleteBin6Line, 
  RiEdit2Line, 
  RiAddLine,
  RiSearchLine,
  RiMore2Line,
  RiEyeLine,
  RiRefreshLine
} from 'react-icons/ri';
import { 
  FiSearch, 
  FiPlus, 
  FiTrash2, 
  FiEdit2, 
  FiToggleLeft, 
  FiToggleRight,
  FiCheckCircle,
  FiXCircle,
  FiGift,
  FiPercent,
  FiTag,
  FiClock,
  FiRefreshCw,
  FiMoreVertical,
  FiAward,
  FiStar,
  FiHeart,
  FiDollarSign,
  FiFilter
} from "react-icons/fi";
import { 
  MdDelete, 
  MdEdit, 
  MdAdd, 
  MdSearch, 
  MdGift,
  MdOutlineDelete,
  MdOutlineEdit,
  MdOutlineAdd,
  MdOutlineSearch,
  MdCardGiftcard,
  MdLocalOffer,
  MdDiscount,
  MdVerified,
  MdStarOutline,
  MdFavorite,
  MdMonetizationOn
} from "react-icons/md";
import { CiFilter } from 'react-icons/ci';
import { LiaEdit } from 'react-icons/lia';

const RewardTable = ({ 
  TbaleName, 
  TbaleHeader, 
  TbaleData, 
  showMenu, 
  viewAction, 
  handleToggle, 
  isMenu, 
  clickedUserId, 
  handleSearch, 
  handleFilter, 
  handleCreateModal 
}) => {
  const ContentActionRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ContentActionRef.current && !ContentActionRef.current.contains(event.target)) {
        showMenu(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  // Status badge component
  const StatusBadge = ({ status }) => {
    if (status === false) {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full">
          <FiCheckCircle className="w-3.5 h-3.5 text-emerald-500" />
          Active
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-rose-700 bg-rose-50 border border-rose-200 rounded-full">
        <FiXCircle className="w-3.5 h-3.5 text-rose-500" />
        Inactive
      </span>
    );
  };

  // Get reward type icon
  const getRewardTypeIcon = (type) => {
    const typeMap = {
      'discount': FiPercent,
      'coupon': FiTag,
      'gift': FiGift,
      'cash': FiDollarSign,
      'points': FiStar,
      'voucher': FiAward,
      'free': FiHeart
    };
    const Icon = typeMap[type?.toLowerCase()] || FiGift;
    return Icon;
  };

  // Get reward type color
  const getRewardTypeColor = (type) => {
    const colorMap = {
      'discount': 'text-emerald-600 bg-emerald-50 border-emerald-200',
      'coupon': 'text-blue-600 bg-blue-50 border-blue-200',
      'gift': 'text-purple-600 bg-purple-50 border-purple-200',
      'cash': 'text-amber-600 bg-amber-50 border-amber-200',
      'points': 'text-rose-600 bg-rose-50 border-rose-200',
      'voucher': 'text-indigo-600 bg-indigo-50 border-indigo-200',
      'free': 'text-pink-600 bg-pink-50 border-pink-200'
    };
    return colorMap[type?.toLowerCase()] || 'text-gray-600 bg-gray-50 border-gray-200';
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    if (handleSearch) handleSearch(e);
  };

  const handleFilterChange = (e) => {
    setFilterStatus(e.target.value);
    if (handleFilter) handleFilter(e);
  };

  return (
    <div className="w-full">
      <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300 border border-gray-100/80 overflow-hidden">
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-4 md:p-5 border-b border-gray-100 bg-gradient-to-r from-gray-50/50 to-white">
          <div className="flex items-center gap-3">
            <div className="p-1.5 bg-gradient-to-br from-[#855DF6]/10 to-[#715EFE]/10 rounded-lg">
              <MdCardGiftcard className="w-4 h-4 text-[#715EFE]" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-800">{TbaleName}</h2>
              <p className="text-xs text-gray-400 hidden sm:block">
                {TbaleData?.length || 0} rewards configured
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            {/* Search */}
            <div className="relative flex-1 sm:min-w-[180px]">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search rewards..."
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#715EFE]/20 focus:border-[#715EFE] transition-all duration-200"
                onChange={handleSearchChange}
              />
            </div>

            {/* Filter */}
            <div className="relative inline-flex items-center border border-gray-200 rounded-lg hover:border-[#715EFE] transition-colors duration-200">
              <div className="px-3 py-2 text-gray-500">
                <FiFilter className="w-4 h-4" />
              </div>
              <select 
                className="w-full px-3 py-2 text-sm text-gray-700 bg-transparent border-none appearance-none focus:ring-0 focus:outline-none pr-8"
                onChange={handleFilterChange}
                value={filterStatus}
              >
                <option value="">All Status</option>
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
              </select>
              <FiSearch className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>

            {/* Create Button */}
            <button 
              className="flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-[#855DF6] to-[#715EFE] rounded-lg hover:shadow-lg hover:shadow-[#715EFE]/25 transition-all duration-200 whitespace-nowrap"
              onClick={() => handleCreateModal('REWARD')}
            >
              <FiPlus className="w-4 h-4" />
              <span>Add New Reward</span>
            </button>
          </div>
        </div>

        {/* Table Section */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-50/80 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-100">
                {TbaleHeader.map((heading, index) => {
                  const headerIcons = {
                    '#': null,
                    'Reward Name': FiGift,
                    'Reward Type': FiTag,
                    'Status': FiToggleLeft,
                    'Associated Reward': FiAward,
                    'Conditions': FiClock,
                    'Action': null
                  };
                  const Icon = headerIcons[heading];
                  return (
                    <td key={index} className="px-3 md:px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        {Icon && <Icon className="w-3.5 h-3.5 text-gray-400" />}
                        <span>{heading}</span>
                      </div>
                    </td>
                  );
                })}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {TbaleData && TbaleData.length > 0 ? (
                TbaleData.map((ele, index) => {
                  const RewardTypeIcon = getRewardTypeIcon(ele.type);
                  const typeColor = getRewardTypeColor(ele.type);
                  
                  return (
                    <tr key={ele.id || index} className="hover:bg-gray-50/70 transition-colors duration-150 group">
                      <td className="px-3 md:px-4 py-3">
                        <span className="text-sm font-medium text-gray-400">
                          {ele.id || index + 1}
                        </span>
                      </td>
                      <td className="px-3 md:px-4 py-3">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#855DF6]/10 to-[#715EFE]/10 flex items-center justify-center">
                            <FiGift className="w-4 h-4 text-[#715EFE]" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-gray-800">
                              {ele.name || '-'}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 md:px-4 py-3">
                        <span className={`
                          inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full border
                          ${typeColor}
                        `}>
                          <RewardTypeIcon className="w-3.5 h-3.5" />
                          {ele.type || 'N/A'}
                        </span>
                      </td>
                      <td className="px-3 md:px-4 py-3">
                        <StatusBadge status={ele?.status} />
                      </td>
                      <td className="px-3 md:px-4 py-3">
                        <span className="text-sm font-medium text-emerald-600">
                          {ele?.reward || '-'}
                        </span>
                      </td>
                      <td className="px-3 md:px-4 py-3">
                        <span className="text-sm text-gray-600">
                          {ele.Conditions || 'None'}
                        </span>
                      </td>
                      <td className="px-3 md:px-4 py-3">
                        <div className="flex items-center gap-1">
                          <button 
                            className="p-1.5 text-rose-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-all duration-200"
                            onClick={() => viewAction(ele)}
                            title="Delete Reward"
                          >
                            <FiTrash2 className="w-4 h-4" />
                          </button>
                          <button 
                            className="p-1.5 text-[#715EFE] hover:text-[#855DF6] hover:bg-[#715EFE]/10 rounded-lg transition-all duration-200"
                            onClick={() => viewAction(ele)}
                            title="Edit Reward"
                          >
                            <FiEdit2 className="w-4 h-4" />
                          </button>
                          <button 
                            className={`p-1.5 rounded-lg transition-all duration-200 ${
                              ele?.status === false
                                ? 'text-amber-400 hover:text-amber-600 hover:bg-amber-50'
                                : 'text-emerald-400 hover:text-emerald-600 hover:bg-emerald-50'
                            }`}
                            onClick={() => handleToggle(ele)}
                            title={ele?.status === false ? 'Deactivate' : 'Activate'}
                          >
                            {ele?.status === false ? (
                              <FiToggleRight className="w-4 h-4" />
                            ) : (
                              <FiToggleLeft className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={TbaleHeader.length} className="px-4 py-12 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <div className="p-4 bg-gray-50 rounded-full">
                        <FiGift className="w-10 h-10 text-gray-300" />
                      </div>
                      <p className="text-sm font-medium text-gray-500">No rewards found</p>
                      <p className="text-xs text-gray-400">Create your first reward</p>
                      <button 
                        className="mt-2 flex items-center gap-2 px-4 py-2 text-sm font-medium text-[#715EFE] border border-[#715EFE] rounded-lg hover:bg-[#715EFE] hover:text-white transition-all duration-200"
                        onClick={() => handleCreateModal('REWARD')}
                      >
                        <FiPlus className="w-4 h-4" />
                        Add New Reward
                      </button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile View for Small Screens */}
        <div className="md:hidden divide-y divide-gray-100">
          {TbaleData && TbaleData.length > 0 ? (
            TbaleData.map((ele, index) => {
              const RewardTypeIcon = getRewardTypeIcon(ele.type);
              const typeColor = getRewardTypeColor(ele.type);
              
              return (
                <div key={ele.id || index} className="p-4 hover:bg-gray-50/70 transition-colors duration-150">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-gray-400">
                        #{ele.id || index + 1}
                      </span>
                      <StatusBadge status={ele?.status} />
                    </div>
                    <div className="flex items-center gap-1">
                      <button 
                        className="p-1.5 text-rose-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg"
                        onClick={() => viewAction(ele)}
                      >
                        <FiTrash2 className="w-4 h-4" />
                      </button>
                      <button 
                        className="p-1.5 text-[#715EFE] hover:text-[#855DF6] hover:bg-[#715EFE]/10 rounded-lg"
                        onClick={() => viewAction(ele)}
                      >
                        <FiEdit2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-500">Name</span>
                      <span className="text-sm font-semibold text-gray-800">
                        {ele.name || '-'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-500">Type</span>
                      <span className={`
                        inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border
                        ${typeColor}
                      `}>
                        <RewardTypeIcon className="w-3 h-3" />
                        {ele.type || 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-500">Reward</span>
                      <span className="text-sm font-medium text-emerald-600">
                        {ele?.reward || '-'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-500">Conditions</span>
                      <span className="text-sm text-gray-600">
                        {ele.Conditions || 'None'}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="p-8 text-center">
              <div className="flex flex-col items-center gap-2">
                <FiGift className="w-10 h-10 text-gray-300" />
                <p className="text-sm text-gray-500">No rewards found</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {TbaleData && TbaleData.length > 0 && (
          <div className="px-4 py-3 bg-gray-50/50 border-t border-gray-100 flex items-center justify-between">
            <span className="text-xs text-gray-500">
              Showing {TbaleData.length} reward{TbaleData.length !== 1 ? 's' : ''}
            </span>
            <span className="text-xs text-gray-400">
              Last updated: {new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
        )}
      </div>
    </div>
  )
}

export default RewardTable